import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Colors } from "../../../configs";
import {
  deleteStoreCreditAction,
  getCustomerByIdAction,
  getStoreCreditHistoryAction,
} from "../../../redux/actions/customerAction";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { formatCurrency, formatDateTime } from "../../../util/common-functions";
import { paymentTypeLabels } from "../../../typings/enum/paymentTypes";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import ConfirmDialog from "../../atoms/confirm-box/confirm-box";
import ManagerCodeModal from "../../common/manager-code-modal/ManagerCodeModal";

export interface IStoreCreditHistoryProps {
  customerID: number;
  isCustomerEdited: boolean;
  resetDirtyState(isDirty: Boolean): void;
}

const StoreCreditHistory: FC<IStoreCreditHistoryProps> = ({
  customerID,
  isCustomerEdited,
  resetDirtyState,
}) => {
  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbarContext();
  const {
    storeCreditHistoryResponse,
    getStoreCreditHistoryLoading,
    deleteStoreCreditLoading,
  } = useAppSelector((state) => state.customer);

  const { user } = useAppSelector((state) => state.auth);
  const createData = (
    dateTime: string,
    addedBy: string,
    paymentType: string,
    amount: number,
    customerId: number,
    noteId: number,
    isDeleted: boolean
  ) => {
    const dateTimeString = formatDateTime(dateTime);
    const paymentTypeString = paymentTypeLabels(paymentType);
    return {
      dateTimeString,
      addedBy,
      paymentTypeString,
      amount,
      customerId,
      noteId,
      isDeleted,
    };
  };

  // Helper function to check if a deposit can be deleted
  const canDeleteDeposit = (depositAmount: number): boolean => {
    if (depositAmount <= 0) return false; // Cannot delete used credits

    const totalDeposits = storeCreditHistoryResponse?.body
      ?.filter(item => item.amount > 0 && !(item.isDeleted || (item as any).deleted))
      .reduce((sum, item) => sum + item.amount, 0) || 0;

    const totalUsed = Math.abs(
      storeCreditHistoryResponse?.body
        ?.filter(item => item.amount < 0 && !(item.isDeleted || (item as any).deleted))
        .reduce((sum, item) => sum + item.amount, 0) || 0
    );

    return (totalDeposits - depositAmount) >= totalUsed;
  };
  const [mgrOpen, setMgrOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    customerId: number;
    noteId: number;
  } | null>(null);

  useEffect(() => {
    customerID && dispatch(getStoreCreditHistoryAction(customerID));
  }, [customerID]);

  const rows = storeCreditHistoryResponse?.body?.map((item) => {
    return createData(
      item.createdTime,
      item.addedBy.firstName + " " + item.addedBy.lastName, // Changed from item.customer to item.addedBy
      item.paymentType,
      item.amount,
      item.customerId, // Use customerId from response
      item.id,
      item.isDeleted || (item as any).deleted || false
    );
  });

  const [visibleItemsLimit, setVisibleItemsLimit] = useState(15);
  const [discardDialogOpen, setDiscardDialogOpen] = useState<{
    open: boolean;
    customerId: number | null;
    noteId: number | null;
  }>({
    open: false,
    customerId: null,
    noteId: null,
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom =
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop <=
      e.currentTarget.clientHeight + 2;

    if (bottom && (rows?.length || 0) > visibleItemsLimit) {
      setVisibleItemsLimit((prev) => prev + 15);
    }
  };

  const handleManagerApproved = () => {
    setMgrOpen(false);
    if (!pendingDelete) return;

    if (isCustomerEdited) {
      setDiscardDialogOpen({
        open: true,
        customerId: pendingDelete.customerId,
        noteId: pendingDelete.noteId,
      });
    } else {
      onDelete(pendingDelete.customerId, pendingDelete.noteId);
      setPendingDelete(null);
    }
  };

  // removed emptyRows logic since it's an infinite scroll list

  const onDelete = (customerId: number, noteId: number) => {
    setDiscardDialogOpen({
      open: false,
      customerId: null,
      noteId: null,
    });
    dispatch(
      deleteStoreCreditAction({ customerId: customerId, noteId: noteId })
    ).then((res) => {
      if (res.payload) {
        if ((res.payload as any).status === "SUCCESS") {
          showSnackbar("Store credit deleted successfully", "success");
        } else {
          showSnackbar("Failed to delete store credit", "error");
        }
        dispatch(getStoreCreditHistoryAction(customerID));
        dispatch(getCustomerByIdAction(customerID.toString()));
      }
    });
    resetDirtyState(false);
  };
  const handleOnClickDelete = (customerId: number, noteId: number, amount: number) => {
    // Validate if deletion is allowed
    if (amount < 0) {
      showSnackbar("Cannot delete used store credit", "error");
      return;
    }

    if (!canDeleteDeposit(amount)) {
      showSnackbar("Cannot delete this deposit. Remaining deposits would be less than total used credits", "error");
      return;
    }

    setPendingDelete({ customerId, noteId });
    setMgrOpen(true);
  };

  return (
    <Box sx={{ backgroundColor: Colors.BACKGROUND_GRAY }}>
      <ConfirmDialog
        open={discardDialogOpen.open}
        title="Discard Confirmation"
        message="If you proceed, the changes you've made for the user will be discarded."
        onCancel={() => {
          setDiscardDialogOpen({ open: false, customerId: null, noteId: null });
          setPendingDelete(null);
        }}
        onConfirm={() =>
          discardDialogOpen.customerId &&
          discardDialogOpen.noteId &&
          onDelete(discardDialogOpen.customerId, discardDialogOpen.noteId)
        }
      />
      <Box
        onScroll={handleScroll}
        sx={{
          height: "53.5rem",
          mt: -2.8,
          mb: -2.8,
          backgroundColor: Colors.BACKGROUND_GRAY,
          overflowY: "scroll",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          scrollbarWidth: "none",
          // background: "red",
        }}
        marginX="-2.4rem"
      >
        <Table
          stickyHeader
          sx={{
            minWidth: "46.4285714rem",
            backgroundColor: Colors.BACKGROUND_GRAY,
          }}
        >
          <TableHead sx={{ backgroundColor: Colors.BACKGROUND_GRAY }}>
            <TableRow
              sx={{
                "& th": {
                  borderBottom: "2px solid #fff",
                },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: Colors.TEXT_GRAY_DARK,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                }}
              >
                Date & Time
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "bold",
                  color: Colors.TEXT_GRAY_DARK,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                }}
              >
                Added By
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "bold",
                  color: Colors.TEXT_GRAY_DARK,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                }}
              >
                Payment Type
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "bold",
                  color: Colors.TEXT_GRAY_DARK,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                }}
              >
                Amount ($)
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: "bold",
                  color: Colors.TEXT_GRAY_DARK,
                  backgroundColor: Colors.BACKGROUND_GRAY,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows ? rows : [])
              .slice(0, visibleItemsLimit)
              .map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    opacity: row.isDeleted ? 0.6 : 1,
                    "& td, & th": {
                      borderBottom: "2px solid #fff",
                      textDecoration: row.isDeleted ? 'line-through !important' : 'none',
                    },
                    "&:last-child td, &:last-child th": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.dateTimeString}
                  </TableCell>
                  <TableCell align="right">{row.addedBy}</TableCell>
                  <TableCell align="right">{row.paymentTypeString}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color:
                        row.amount < 0
                          ? Colors.ERROR_RED
                          : row.amount > 0
                            ? Colors.SUCCESS_GREEN
                            : Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    {formatCurrency({
                      amount: row.amount,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() =>
                        handleOnClickDelete(row.customerId, row.noteId, row.amount)
                      }
                      disabled={
                        deleteStoreCreditLoading ||
                        row.amount < 0 ||
                        row.isDeleted ||
                        !canDeleteDeposit(row.amount)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {getStoreCreditHistoryLoading && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  style={{ height: 53 * 11, textAlign: "center" }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <ManagerCodeModal
        open={mgrOpen}
        onClose={() => {
          setMgrOpen(false);
          setPendingDelete(null);
        }}
        title="Credit History"
        task="delete this store credit"
        onDone={handleManagerApproved}
        actionType="DELETE_STORE_CREDIT"
        requestedByUserId={user?.id}
      />
    </Box>
  );
};

export default StoreCreditHistory;
